import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Lead, LeadStatus } from "@/components/lead-card"; // Assuming LeadStatus might be used or can be pruned
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { CalendarIcon, Filter } from "lucide-react";
import { AppSidebar } from "@/components/app-sidebar";
import { format, addDays, startOfDay, endOfDay, subDays } from "date-fns";
import { de } from "date-fns/locale";
import { fetchLeadsForCurrentUser, deleteLead as deleteLeadService } from "@/services/leadsService"; // Added deleteLeadService
import { UserLeadsSummary } from "@/components/user-leads-summary";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// User data interface - can be moved to a shared types file if not already
interface UserData {
  id: string;
  name: string;
  email: string;
  role: string;
  leads: Lead[];
  position?: string; // Added position
}

export default function TeamOverviewPage() {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [allLeads, setAllLeads] = useState<Lead[]>([]); // Store all leads for context
  const [filteredUsers, setFilteredUsers] = useState<UserData[]>([]); // Users with leads filtered by date
  const [allUsers, setAllUsers] = useState<UserData[]>([]); // All users with all their leads
  const [isLoading, setIsLoading] = useState(true);
  const [dateRange, setDateRange] = useState<{
    from: Date | undefined;
    to: Date | undefined;
  }>({
    from: undefined, // Default to last 30 days or as per requirement
    to: undefined
  });
  const [filtersVisible, setFiltersVisible] = useState(false);

  // Load users and their leads
  const loadUsersAndLeads = async () => {
    setIsLoading(true);
    try {
      // Fetch leads based on current user's role and tenant
      // Admin/Developer: see all tenant leads
      // Berater: see only their assigned leads
      const fetchedLeads = await fetchLeadsForCurrentUser(
        currentUser.id,
        currentUser.role,
        currentUser.tenantId
      );
      setAllLeads(fetchedLeads);

      // Fetch users for current tenant only
      const usersRef = collection(db, "users");
      let usersQuery;

      // For legacy tenant, fetch all users and filter client-side
      // For new tenants, filter by tenantId
      if (currentUser.tenantId === "legacy") {
        usersQuery = usersRef;
      } else {
        usersQuery = query(usersRef, where("tenantId", "==", currentUser.tenantId));
      }

      const usersSnapshot = await getDocs(usersQuery);
      const usersDataResult: UserData[] = []; // Renamed to avoid conflict with users state if any

      usersSnapshot.forEach(doc => {
        const userDataFromDoc = doc.data() as any; // Renamed to avoid conflict
        const userRole = userDataFromDoc.role || "user";

        // For legacy tenant, exclude users from other tenants
        if (currentUser.tenantId === "legacy") {
          if (userDataFromDoc.tenantId && userDataFromDoc.tenantId !== "legacy") {
            return; // Skip users from other tenants
          }
        }

        // Filter out developer accounts - they should not be visible in Team Overview
        if (userRole === "developer") {
          return;
        }

        // Filter out deactivated users
        if (userDataFromDoc.active === false) {
          return;
        }

        const userLeads = fetchedLeads.filter(lead => lead.assignedTo === doc.id);

        usersDataResult.push({
          id: doc.id,
          name: userDataFromDoc.name || "Unbenannter Benutzer",
          email: userDataFromDoc.email || "",
          role: userRole,
          position: userDataFromDoc.position || '', // Added position mapping
          leads: userLeads
        });
      });

      console.log('Team Overview - Loaded users:', usersDataResult.length);
      console.log('Team Overview - User data:', usersDataResult);
      console.log('Team Overview - Current user role:', currentUser?.role);

      setAllUsers(usersDataResult);
      setFilteredUsers(usersDataResult);

    } catch (error) {
      console.error("Error loading data for Team Overview:", error);
      toast({
        title: "Fehler beim Laden der Daten",
        description: "Die Teamübersicht konnte nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser?.role === 'admin' || currentUser?.role === 'developer') {
      loadUsersAndLeads();
    } else {
      // Redirect or show an unauthorized message if not admin/developer
      setIsLoading(false);
      // Consider redirecting: history.push('/dashboard'); or similar
    }
  }, [currentUser, toast]);


  // Filter users' leads based on dateRange
  useEffect(() => {
    if (!allUsers.length) return;

    const usersWithFilteredLeads = allUsers.map(user => {
      if (!dateRange.from && !dateRange.to) {
        return user; // No date filter, return user with all leads
      }

      const filteredLeads = user.leads.filter(lead => {
        const leadDate = lead.createdAt
          ? new Date(lead.createdAt)
          : lead.lastUpdated
            ? new Date(lead.lastUpdated)
            : null;

        if (!leadDate) return false; // If no date, exclude

        const fromDate = dateRange.from ? startOfDay(dateRange.from) : undefined;
        const toDate = dateRange.to ? endOfDay(dateRange.to) : undefined;

        if (fromDate && toDate) {
          return leadDate >= fromDate && leadDate <= toDate;
        } else if (fromDate) {
          return leadDate >= fromDate;
        } else if (toDate) {
          return leadDate <= toDate;
        }
        return true; // Should not happen if from or to is defined
      });
      return { ...user, leads: filteredLeads };
    });

    setFilteredUsers(usersWithFilteredLeads);

  }, [dateRange, allUsers]);


  const handleLeadDelete = async (leadId: string) => {
    try {
      await deleteLeadService(leadId);
      toast({
        title: "Lead gelöscht",
        description: "Der Lead wurde erfolgreich entfernt.",
      });
      // Refresh data
      loadUsersAndLeads();
    } catch (error) {
      toast({
        title: "Fehler beim Löschen",
        description: "Der Lead konnte nicht gelöscht werden.",
        variant: "destructive",
      });
    }
  };

  // Placeholder for potential future user selection, not used from UserLeadsSummary in this context
  const handleUserSelect = (userId: string) => {
    // Currently, clicking "Details" in UserLeadsSummary on Dashboard goes to a user-specific view.
    // For TeamOverviewPage, this might be different or not needed.
    // For now, it can simply log or do nothing, as the main view is the summary.
    console.log("User selected in Team Overview (for potential detail view):", userId);
    toast({
      title: "Benutzer ausgewählt",
      description: `Details für Benutzer ID ${userId} könnten hier angezeigt werden.`,
    });
  };


  if (isLoading) {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Teamübersicht wird geladen...</p>
          </div>
        </div>
      </div>
    );
  }

  if (currentUser?.role !== 'admin' && currentUser?.role !== 'developer') {
    return (
      <div className="flex h-screen overflow-hidden bg-background">
        <AppSidebar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">Zugriff verweigert. Diese Seite ist nur für Administratoren und Entwickler.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <AppSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="py-4 px-6 border-b">
          <div className="flex flex-wrap items-center justify-between mb-4 gap-2">
            <div>
              <h1 className="text-2xl font-bold">Team Übersicht</h1>
              <p className="text-muted-foreground">Leistungsübersicht aller Teammitglieder im ausgewählten Zeitraum</p>
            </div>
            <div className="flex items-center gap-3">
              <Popover open={filtersVisible} onOpenChange={setFiltersVisible}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={dateRange.from || dateRange.to ? "border-primary" : ""}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Zeitraum filtern
                    {(dateRange.from || dateRange.to) && (
                      <Badge variant="secondary" className="ml-2">
                        {dateRange.from && dateRange.to
                          ? `${format(dateRange.from, "dd.MM.yy", { locale: de })} - ${format(dateRange.to, "dd.MM.yy", { locale: de })}`
                          : dateRange.from
                            ? `Ab ${format(dateRange.from, "dd.MM.yy", { locale: de })}`
                            : dateRange.to
                              ? `Bis ${format(dateRange.to!, "dd.MM.yy", { locale: de })}`
                              : "Alle Daten"
                        }
                      </Badge>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <div className="p-4 space-y-4">
                    <h4 className="font-medium">Zeitraum auswählen</h4>
                    <div className="grid gap-2">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Von</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant={"outline"}
                                size="sm"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !dateRange.from && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateRange.from ? (
                                  format(dateRange.from, "dd.MM.yyyy", { locale: de })
                                ) : (
                                  <span>Startdatum</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={dateRange.from}
                                onSelect={(date) => {
                                  if (date) {
                                    const fromDate = startOfDay(date);
                                    setDateRange(prev => ({
                                      ...prev,
                                      from: fromDate,
                                      to: prev.to && prev.to < fromDate ? undefined : prev.to
                                    }));
                                  } else {
                                    setDateRange(prev => ({ ...prev, from: undefined }));
                                  }
                                }}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-medium">Bis</label>
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant={"outline"}
                                size="sm"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !dateRange.to && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {dateRange.to ? (
                                  format(dateRange.to, "dd.MM.yyyy", { locale: de })
                                ) : (
                                  <span>Enddatum</span>
                                )}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={dateRange.to}
                                onSelect={(date) => {
                                  if (date) {
                                    const toDate = endOfDay(date);
                                    setDateRange(prev => ({
                                      ...prev,
                                      to: toDate,
                                      from: prev.from && prev.from > toDate ? undefined : prev.from
                                    }));
                                  } else {
                                    setDateRange(prev => ({ ...prev, to: undefined }));
                                  }
                                }}
                                disabled={(date) => dateRange.from ? date < startOfDay(dateRange.from) : false}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setDateRange({ from: undefined, to: undefined });
                          setFiltersVisible(false); // Close popover on reset
                        }}
                      >
                        Zurücksetzen
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setFiltersVisible(false)}
                      >
                        Anwenden & Schließen
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        <div className="p-6">
          {filteredUsers.length > 0 ? (
            <UserLeadsSummary
              users={filteredUsers}
              onLeadDelete={handleLeadDelete}
            />
          ) : (
            <div className="text-center py-10">
              <p className="text-muted-foreground">Keine Benutzerdaten für den ausgewählten Zeitraum oder keine Benutzer vorhanden.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 
